import { useState, useEffect } from 'react';
import { format, addMonths, addDays } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTranslation } from '@/contexts/LanguageContext';
import { useDateLocale } from '@/hooks/useDateLocale';
import { YearPicker } from './YearPicker';
import { MonthYearPicker } from './MonthYearPicker';

interface GoalDateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  period: 'daily' | 'monthly' | 'quarterly' | 'semiannual' | 'yearly';
  hasConflict?: boolean;
  goalType?: 'arr' | 'mrr' | 'customers' | 'churn';
}

export function GoalDateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  period,
  hasConflict,
  goalType,
}: GoalDateRangePickerProps) {
  const { t } = useTranslation();
  const locale = useDateLocale();
  const [openStart, setOpenStart] = useState(false);
  const [openEnd, setOpenEnd] = useState(false);

  // Handle ARR - year selection
  const handleYearChange = (year: number) => {
    onStartDateChange(new Date(year, 0, 1));
    onEndDateChange(new Date(year, 11, 31));
  };

  // Handle MRR - month/year selection
  const handleMonthChange = (month: number, year: number) => {
    if (month && year) {
      onStartDateChange(new Date(year, month - 1, 1));
      const lastDay = new Date(year, month, 0).getDate();
      onEndDateChange(new Date(year, month - 1, lastDay));
    }
  };

  // Auto-suggest end date based on period when start date changes
  useEffect(() => {
    if (startDate && !endDate) {
      let suggestedEndDate: Date;
      switch (period) {
        case 'daily':
          // For daily, end date is the same as start date
          suggestedEndDate = startDate;
          break;
        case 'monthly':
          suggestedEndDate = addMonths(startDate, 1);
          break;
        case 'quarterly':
          suggestedEndDate = addMonths(startDate, 3);
          break;
        case 'semiannual':
          suggestedEndDate = addMonths(startDate, 6);
          break;
        case 'yearly':
          suggestedEndDate = addMonths(startDate, 12);
          break;
        default:
          return;
      }
      // For daily, use the same date. For others, subtract 1 day to make it inclusive
      onEndDateChange(period === 'daily' ? suggestedEndDate : addDays(suggestedEndDate, -1));
    }
  }, [startDate, period, endDate, onEndDateChange]);

  const isInvalidRange = startDate && endDate && endDate <= startDate;

  // Render ARR year picker
  if (goalType === 'arr') {
    return (
      <div className="space-y-4">
        <YearPicker
          selectedYear={startDate ? startDate.getFullYear() : null}
          onYearChange={handleYearChange}
        />
        {hasConflict && (
          <Alert variant="destructive">
            <AlertDescription>{t('dateConflict')}</AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  // Render MRR month/year picker
  if (goalType === 'mrr') {
    return (
      <div className="space-y-4">
        <MonthYearPicker
          selectedMonth={startDate ? startDate.getMonth() + 1 : null}
          selectedYear={startDate ? startDate.getFullYear() : null}
          onMonthChange={(month) => {
            if (startDate) {
              handleMonthChange(month, startDate.getFullYear());
            }
          }}
          onYearChange={(year) => {
            if (startDate) {
              handleMonthChange(startDate.getMonth() + 1, year);
            }
          }}
        />
        {hasConflict && (
          <Alert variant="destructive">
            <AlertDescription>{t('dateConflict')}</AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  // Default date range picker for other goal types
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('startDate')}</Label>
          <Popover open={openStart} onOpenChange={setOpenStart}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !startDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, 'dd/MM/yyyy', { locale }) : <span>{t('selectDate')}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate || undefined}
                onSelect={(date) => {
                  onStartDateChange(date || null);
                  setOpenStart(false);
                }}
                locale={locale}
                initialFocus
                className={cn('p-3 pointer-events-auto')}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>{t('endDate')}</Label>
          <Popover open={openEnd} onOpenChange={setOpenEnd}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !endDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, 'dd/MM/yyyy', { locale }) : <span>{t('selectDate')}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate || undefined}
                onSelect={(date) => {
                  onEndDateChange(date || null);
                  setOpenEnd(false);
                }}
                disabled={(date) => (startDate ? date <= startDate : false)}
                locale={locale}
                initialFocus
                className={cn('p-3 pointer-events-auto')}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {isInvalidRange && (
        <Alert variant="destructive">
          <AlertDescription>{t('invalidDateRange')}</AlertDescription>
        </Alert>
      )}

      {hasConflict && !isInvalidRange && (
        <Alert variant="destructive">
          <AlertDescription>{t('dateConflict')}</AlertDescription>
        </Alert>
      )}

      {startDate && endDate && !isInvalidRange && !hasConflict && (
        <div className="text-sm text-muted-foreground">
          {t('validityPeriod')}: {format(startDate, 'dd/MM/yyyy', { locale })} - {format(endDate, 'dd/MM/yyyy', { locale })}
        </div>
      )}
    </div>
  );
}
